import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import QRCode from "qrcode.react";
import { useMedia } from "use-media";
import { media } from "@nju/css";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { ScanEsimQRCode } from "@nju/dev-tip";
import { getFeatureFlagValue } from "@nju/feature-flags";
import { FAQButton } from "@nju/feature-portal-help";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { Button, CircleLoader, Heading, Panel, Separator, Text } from "@nju/ui";
import { changeEsimCardReadyMachine } from "./changeEsimCardReady.machine";

export function ChangeEsimCardReadyPage() {
  const [qrCodeValue, setQrCodeValue] = React.useState("");
  const isMobile = useMedia(media.onlyMobile);
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.wymianaKarty.esim.kartaGotowa
    .input(router.query)
    .unwrap();
  const { mobileProducts } = useMemberProducts();
  const { changeSimEsim } = useMemberOrchestrator();
  const orderSimNumber = changeSimEsim?.orderSimNumber;
  const characteristicSim = mobileProducts?.simCard?.characteristic?.sim;
  const prdQRCodeInput = "LPA:1$sm-v4-009-pla-gtm.pr.go-esim.com$";
  const devQRCodeInput = "LPA:1$sm-v4-009-plppa-gtm.pr.go-esim.com$";
  const isPrd = process.env.NEXT_PUBLIC_APP_ENV === "prd";
  const isDevTipFlagEnabled = getFeatureFlagValue("devTip") === "1";

  const [current] = useMachine(changeEsimCardReadyMachine, {
    context: {
      userId: params?.userId,
      iccId: orderSimNumber || characteristicSim,
    },
  });

  const { context, matches } = current;

  React.useEffect(() => {
    const activeQRCode = isPrd ? prdQRCodeInput : devQRCodeInput;

    setQrCodeValue(activeQRCode + context.matchingId);
  }, [context.matchingId, isPrd]);

  const handleGoBack = React.useCallback(() => {
    router.back();
  }, [router]);

  if (matches("init")) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircleLoader />
      </div>
    );
  }

  return (
    <Panel>
      <Heading
        align="center"
        font="ttfirs"
        tone="neutral"
        variant="h2"
        weight="bold"
      >
        karta eSIM gotowa
      </Heading>
      <Separator size="s" tone="invisible" />
      <div className="flex justify-center">
        <Icon
          className="w-[60px] h-[60px] from-tablet:w-[90px] from-tablet:h-[90px]"
          name="sim-card-incircle"
        />
      </div>
      <Separator size="s" tone="invisible" />
      <div className="flex flex-col justify-center">
        <Text align="center" size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
          QR kod z Twoją kartą eSIM jest już dostępny. Aby ją aktywować na
          urządzeniu, wejdź w jego ustawienia i postępuj zgodnie z instrukcją.
        </Text>
        <Separator size="xs" tone="invisible" />
        <div className="flex items-center justify-center">
          <FAQButton
            categoryId="NJU_WWW_INSTALL_ESIM"
            title="jak zainstalować kartę eSIM?"
          />
        </div>
      </div>
      <Separator size="m" />
      <div className="flex justify-center items-center my-0 mx-auto from-tablet:w-[562px]">
        <QRCode
          data-cy="qr-code"
          renderAs="svg"
          size={isMobile ? 200 : 562}
          value={qrCodeValue}
        />
      </div>
      <Separator size="xs" tone="invisible" />
      <div className="flex justify-center">
        <Text align="center" size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
          Otwórz na telefonie aparat i skieruj go na QR kod. Jeśli aparat go nie
          skanuje, ściągnij aplikację do skanowania QR kodów z Google Play lub
          App Store.
        </Text>
      </div>
      <Separator size="m" />
      <div className="flex justify-center only-mobile:flex-col">
        <Button onPress={handleGoBack} size={{ "@onlyMobile": "s" }}>
          zamknij
        </Button>
      </div>
      {isDevTipFlagEnabled && (
        <>
          <Separator size="s" tone="invisible" />
          <div className="flex justify-center">
            <ScanEsimQRCode iccId={orderSimNumber || characteristicSim} />
          </div>
        </>
      )}
    </Panel>
  );
}
